import { IVat } from './IVat';

/**
 * The label for a VAT rate (backend #682 decision 3).
 *
 * A plain function rather than a pipe: nothing renders a VAT row in a template. Both consumers —
 * Kendo's `textField`, which reads a property, and the vocabulary grid's label list — build the
 * string in component code, and a pipe nobody could call would be dead code.
 *
 * VAT is one of the two closed sets the platform deliberately does **not** translate — it travels
 * as `isUndetermined` + `percentage` and the clients write the words. So `shortName` („Unbestimmt")
 * and `description` are the backend's own German prose: admin data, not a label, and rendering
 * them is how „Unbestimmt" ended up on an English page.
 *
 * The undetermined sentinel (#579) is a real row with a placeholder percentage, which is why the
 * flag decides and not the number: a genuine 0 % rate exists beside it and must read as „0 %".
 */
export function vatLabel(
  vat: Pick<IVat, 'percentage' | 'isUndetermined'> | null | undefined,
): string {
  if (!vat) {
    return '';
  }
  if (vat.isUndetermined) {
    return $localize`:@@vat.undetermined:Unbestimmt`;
  }
  return vatPercentageLabel(vat.percentage);
}

/**
 * A bare percentage as a label — for the places that hold a rate rather than a VAT row, such as
 * the resolution vocabulary, whose targets are the percentages themselves.
 */
export function vatPercentageLabel(percentage: number | null | undefined): string {
  return percentage == null ? '' : $localize`:@@vat.percentage:${percentage}:percentage: %`;
}
